
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

interface Transfer {
  destination: string;
  price: number;
}

const popularTransfers: Transfer[] = [
  { destination: 'Les Deux Alpes', price: 267.95 },
  { destination: 'Val Thorens', price: 404.95 },
  { destination: "Alpe d'Huez", price: 254.95 },
  { destination: "Val d'Isère", price: 397.95 },
  { destination: 'Tignes', price: 404.95 },
  { destination: 'Méribel', price: 418.95 },
  { destination: 'La Plagne', price: 425.95 },
  { destination: 'Saint-Bon-Tarentaise', price: 469.95 },
  { destination: 'Courchevel', price: 358.95 },
  { destination: 'Les Menuires', price: 390.95 },
  { destination: 'Genève Aéroport', price: 483.95 },
  { destination: 'Grenoble', price: 131.95 },
  { destination: 'Les Arcs', price: 418.95 },
  { destination: 'Grenoble Train Station', price: 131.95 },
  { destination: 'Vaujany', price: 306.95 },
  { destination: 'Chamrousse', price: 184.95 },
  { destination: 'Oz-en-Oisans', price: 242.95 },
  { destination: 'Val Claret', price: 432.95 },
  { destination: 'Les Arcs (1950 / 2000)', price: 418.95 },
  { destination: 'La Tania', price: 427.95 },
];

const featuredDestinations = [
  {
    title: 'Grenoble Taxi',
    description: 'Voyagez à travers Grenoble en sélectionnant l\'un de nos transferts prédéfinis.',
    image: 'https://public.readdy.ai/ai/img_res/2abbb87bd2b730b2c0b2fd723acebbbe.jpg',
    link: '/reservation?destination=Grenoble'
  },
  {
    title: "Grenoble Airport Vers Alpe d'Huez",
    description: 'Transfert direct vers la station ensoleillée',
    image: 'https://readdy.ai/api/search-image?query=alpe%20huez%20ski%20resort%20sunny%20slopes%20snowy%20mountains%20grandes%20rousses%20winter%20scenery%20skiers%20pristine%20snow%20blue%20sky%20clean%20simple%20background&width=800&height=600&seq=alpedhuez-transfer-001&orientation=landscape',
    link: '/reservation?destination=Alpe+d\'Huez'
  },
  {
    title: 'Grenoble Airport Navette',
    description: 'Service de navette régulier vers le centre-ville',
    image: 'https://readdy.ai/api/search-image?query=luxury%20mercedes%20van%20black%20vehicle%20mountain%20road%20french%20alps%20scenic%20drive%20professional%20transport%20service%20clean%20simple%20background&width=800&height=600&seq=grenoble-shuttle-001&orientation=landscape',
    link: '/reservation?from=Grenoble+Airport'
  },
  {
    title: 'Grenoble Airport Vers Val Thorens',
    description: 'Vers la plus haute station d\'Europe',
    image: 'https://readdy.ai/api/search-image?query=val%20thorens%20highest%20ski%20resort%20europe%20snowy%20glacier%20slopes%20trois%20vallees%20dramatic%20mountain%20peaks%20winter%20landscape%20pristine%20snow%20blue%20sky%20clean%20simple%20background&width=800&height=600&seq=valthorens-transfer-002&orientation=landscape',
    link: '/reservation?destination=Val+Thorens'
  },
  {
    title: 'Grenoble Airport Vers Les Deux Alpes',
    description: 'Transfert vers le glacier des Deux Alpes',
    image: 'https://readdy.ai/api/search-image?query=les%20deux%20alpes%20ski%20resort%20glacier%20skiing%20snowy%20slopes%20grandes%20rousses%20mountains%20winter%20scenery%20pristine%20snow%20blue%20sky%20clean%20simple%20background&width=800&height=600&seq=les2alpes-transfer-002&orientation=landscape',
    link: '/reservation?destination=Les+Deux+Alpes'
  }
];

export default function GrenobleAirportPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [tripType, setTripType] = useState<'round-trip' | 'one-way'>('round-trip');
  const [passengers, setPassengers] = useState(1);
  
  useSEO({
    title: 'Transfert Aéroport Grenoble vers Stations de Ski | Voyage Elite Premium',
    description: 'Transfert privé depuis l\'aéroport de Grenoble (GNB) vers Les Deux Alpes, Alpe d\'Huez, Val Thorens et toutes les stations des Alpes. Service premium 24/7.',
    keywords: 'transfert Grenoble, navette Grenoble aéroport, VTC Grenoble Alpe d\'Huez, transport Grenoble Les Deux Alpes',
    canonical: '/transfert-aeroport-grenoble',
    ogTitle: 'Transfert Aéroport Grenoble - Stations de Ski | Alpine Transfer',
    ogDescription: 'Service de transfert privé premium depuis Grenoble vers les stations de ski des Alpes',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/transfert-aeroport-grenoble#webpage`,
          url: `${siteUrl}/transfert-aeroport-grenoble`,
          name: 'Transferts depuis Grenoble Aéroport (GNB)',
          description: 'Service de transfert privé premium depuis l\'aéroport de Grenoble vers toutes les stations de ski',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [featuredRef, featuredInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [transfersRef, transfersInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION WITH BOOKING FORM */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=grenoble%20airport%20terminal%20modern%20architecture%20isere%20valley%20french%20alps%20mountains%20background%20blue%20sky%20aircraft%20professional%20aviation%20photography%20elegant%20clean%20simple%20background&width=1920&height=1080&seq=grenoble-airport-hero-002&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-8"
          >
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Grenoble Aéroport (GNB)<br />
              <span className="text-alpine-gold">Transferts</span>
            </h1>
            <p className="text-slate-200 font-inter text-lg lg:text-xl max-w-2xl mx-auto">
              À vos côtés depuis 2008
            </p>
          </motion.div>

          {/* BOOKING FORM */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 max-w-4xl mx-auto"
          >
            {/* Trip Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setTripType('round-trip')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'round-trip'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller-retour
              </button>
              <button
                onClick={() => setTripType('one-way')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'one-way'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller simple
              </button>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* From Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2">De</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    value="Grenoble Aéroport (GNB)"
                    readOnly
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-night-blue bg-slate-50 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Spacer Icon */}
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-alpine-gold/10 flex items-center justify-center">
                  <i className="ri-arrow-down-line text-alpine-gold text-xl"></i>
                </div>
              </div>

              {/* To Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2">Vers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Sélectionnez votre destination"
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-700 font-inter font-medium mb-2">Départ</label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                      <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                    </div>
                    <input
                      type="date"
                      className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                    />
                  </div>
                </div>
                {tripType === 'round-trip' && (
                  <div>
                    <label className="block text-slate-700 font-inter font-medium mb-2">Retour</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                      </div>
                      <input
                        type="date"
                        className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Passengers */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2">Passagers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-line text-alpine-gold text-xl"></i>
                  </div>
                  <select
                    value={passengers}
                    onChange={(e) => setPassengers(Number(e.target.value))}
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer appearance-none"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'passager' : 'passagers'}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none"></i>
                </div>
              </div>

              {/* Submit Button */}
              <Link
                to="/reservation"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-5 bg-alpine-gold text-night-blue rounded-xl font-inter font-bold text-lg hover:bg-night-blue hover:text-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
                Voir les tarifs
              </Link>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="mt-10 flex flex-wrap justify-center gap-8 text-white"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-shield-check-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Des millions de personnes</p>
                <p className="font-inter text-xs text-slate-300">nous font confiance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Service d'assistance</p>
                <p className="font-inter text-xs text-slate-300">24h/24 et 7j/7</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-lock-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Personnalisé</p>
                <p className="font-inter text-xs text-slate-300">et sécurisé</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS SECTION */}
      <section ref={featuredRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={featuredInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Explorez Grenoble avec <span className="text-alpine-gold">Alpine Transfer</span>
            </h2>
            <p className="text-slate-600 font-inter text-lg max-w-3xl mx-auto">
              Transferts sans faille depuis Grenoble Aéroport — où chaque voyage commence avec confort et confiance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredDestinations.map((dest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={featuredInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 * index }}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={dest.image} 
                    alt={dest.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="font-inter text-white text-xl font-bold mb-2">
                    {dest.title}
                  </h3>
                  <p className="text-slate-200 font-inter text-sm mb-4">
                    {dest.description}
                  </p>
                  <Link
                    to={dest.link}
                    className="inline-flex items-center gap-2 text-alpine-gold font-inter text-sm font-semibold group-hover:gap-3 transition-all"
                  >
                    Découvrez plus
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR TRANSFERS SECTION */}
      <section ref={transfersRef} className="py-24 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={transfersInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Les transferts tendances vers<br />
              <span className="text-alpine-gold">Grenoble Aéroport (GNB)</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularTransfers.map((transfer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={transfersInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.03 * index }}
                className="group bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-alpine-gold hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-slate-500 font-inter text-xs mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>Grenoble Aéroport (GNB)</span>
                    </div>
                    <h3 className="font-inter text-night-blue font-bold text-lg mb-1 group-hover:text-alpine-gold transition-colors">
                      {transfer.destination}
                    </h3>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-alpine-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-alpine-gold/20 transition-colors">
                    <i className="ri-skiing-line text-alpine-gold text-xl"></i>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-slate-500 font-inter text-xs mb-2">Prix à partir de</p>
                  <p className="font-playfair text-alpine-gold font-bold text-2xl mb-4">
                    EUR {transfer.price.toFixed(2)}
                  </p>
                  <Link
                    to="/reservation"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-night-blue text-white rounded-xl font-inter font-medium text-sm hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
                  >
                    Réserver
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT INFO SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Grenoble Aéroport (GNB)
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <p className="text-slate-700 font-inter text-lg leading-relaxed mb-6">
              Grenoble, France, is a cosmopolitan city surrounded by 3 large mountain ranges. You can imagine the scenery is spectacular! Grenoble is a great place for active holidays but also for simply sightseeing. Skiing, rock climbing, museums, the Bastille fortress, cable car ride – these are just some of the must-dos in the area.
            </p>
            
            <p className="text-slate-700 font-inter text-lg leading-relaxed">
              After you land at Grenoble Airport, you will need to reach the city center. The most convenient way would be to take an airport transfer. Make your travel experience easy and hassle-free. Have a driver wait for you upon landing, and head straight to your hotel. Don't lose your precious time or money. Enjoy your trip to the fullest.
            </p>
            
            <div className="mt-8 pt-8 border-t border-slate-200">
              <h3 className="font-playfair text-2xl font-bold text-night-blue mb-6">
                Prise en charge à l'aéroport
              </h3>
              <p className="text-slate-700 font-inter text-base leading-relaxed">
                Évitez la file d'attente des taxis grâce à une course pré-réservée à des tarifs compétitifs.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section ref={benefitsRef} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Ce que nous offrons
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'Chauffeurs locaux professionnels',
                description: 'Expérimentés et courtois pour votre confort'
              },
              {
                icon: 'ri-price-tag-line',
                title: 'Tarifs fixes et transparents',
                description: 'Sans frais cachés, prix garantis'
              },
              {
                icon: 'ri-flight-takeoff-line',
                title: 'Suivi des vols',
                description: 'Prises en charge ponctuelles garanties'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Assistance 24/7',
                description: 'Annulations simples et support continu'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={benefitsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.1 * index }}
                className="bg-white border-2 border-slate-200 rounded-2xl p-8 text-center hover:border-alpine-gold hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-alpine-gold text-3xl`}></i>
                </div>
                <h3 className="font-inter text-night-blue font-bold text-lg mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 font-inter text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WORLDWIDE SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-alpine-gold/10 rounded-full mb-8">
              <i className="ri-global-line text-alpine-gold text-2xl"></i>
              <span className="font-inter font-bold text-night-blue">Présent dans le monde entier</span>
            </div>
            
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Découvrez tous les aéroports<br />
              <span className="text-alpine-gold">à disposition</span>
            </h2>
            
            <Link
              to="/transferts"
              className="inline-flex items-center gap-3 px-10 py-5 bg-night-blue text-white rounded-full font-inter font-bold text-lg hover:bg-alpine-gold hover:text-night-blue transition-all shadow-xl whitespace-nowrap cursor-pointer mt-8"
            >
              Découvrez plus
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-6">
              Réservez votre transfert depuis<br />
              <span className="text-alpine-gold">Grenoble Aéroport</span>
            </h2>
            <p className="text-slate-300 font-inter text-xl mb-10">
              Confirmation immédiate • Assistance 24/7 • Annulation simple
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-3 px-12 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-xl hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-2xl"></i>
                Réserver en ligne
              </Link>
              <a
                href="tel:+33605606006"
                className="inline-flex items-center gap-3 px-12 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-xl hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-2xl"></i>
                +33 6 05 60 60 06
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-slate-300">
              <div className="flex items-center gap-2">
                <i className="ri-whatsapp-line text-green-400 text-2xl"></i>
                <a href="https://wa.me/33605606006" target="_blank" rel="noopener noreferrer" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  WhatsApp disponible
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-alpine-gold text-2xl"></i>
                <a href="mailto:contac.driver@outlook.fr" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  contac.driver@outlook.fr
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
